<script setup lang="ts">
import { DestinyMatrix } from "@/types/destinyMatrix";
import {
  computed,
  defineProps,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  ref,
} from "vue";
import CardComponent from "@/views/Calculators/DestinyMatrix/components/CardComponent.vue";
import schema from "@/assets/calculators/destiny-matrix/shema.png";
import { relativeMatrixPoints } from "@/constants/destinyMatrix";

const props = defineProps<{
  matrix: DestinyMatrix | undefined;
}>();

const containerRef = ref<HTMLDivElement | null>(null);
const stageRef = ref<HTMLDivElement | null>(null);
const schemaImage = ref<HTMLImageElement | null>(null);
const schemaItemMultiply = ref(1);

const multiplyBreakpoints = [
  { max: 428, value: 0.65 },
  { max: 530, value: 0.8 },
  { max: 677, value: 1.1 },
  { max: Infinity, value: 1.25 },
];

function loadDiagramImageAsset() {
  const img = new Image();
  img.onload = () => {
    schemaImage.value = img;
  };
  img.src = schema;
}

function updateStageSize() {
  const el = containerRef.value;
  if (!el) return;

  const containerBox = el.getBoundingClientRect();
  stageWidth.value = containerBox.width;
  stageHeight.value = containerBox.width;

  const match = multiplyBreakpoints.find((bp) => containerBox.width <= bp.max);
  if (match) {
    schemaItemMultiply.value = match.value;
  }
}

onBeforeMount(() => {
  loadDiagramImageAsset();
});

onMounted(() => {
  updateStageSize();
  window.addEventListener("resize", updateStageSize);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", updateStageSize);
});

const flatMatrix = computed<Record<string, number>>(() => {
  const matrix = props.matrix;
  if (matrix === undefined) return {} as Record<string, number>;

  return {
    center: matrix.center,
    "r1.l": matrix.r1.l,
    "r1.r": matrix.r1.r,
    "r1.t": matrix.r1.t,
    "r1.b": matrix.r1.b,
    "r1.tl": matrix.r1.tl,
    "r1.bl": matrix.r1.bl,
    "r1.br": matrix.r1.br,
    "r1.tr": matrix.r1.tr,
    "r2.l": matrix.r2.l,
    "r2.r": matrix.r2.r,
    "r2.t": matrix.r2.t,
    "r2.b": matrix.r2.b,
    "r2.tl": matrix.r2.tl,
    "r2.bl": matrix.r2.bl,
    "r2.br": matrix.r2.br,
    "r2.tr": matrix.r2.tr,
    "r3.l": matrix.r3.l,
    "r3.r": matrix.r3.r,
    "r3.t": matrix.r3.t,
    "r3.b": matrix.r3.b,
    "r3.tl": matrix.r3.tl,
    "r3.bl": matrix.r3.bl,
    "r3.br": matrix.r3.br,
    "r3.tr": matrix.r3.tr,
    "middle_points.l": matrix.middle_points.l,
    "middle_points.t": matrix.middle_points.t,
    "middle_points.r": matrix.middle_points.r,
    "middle_points.right_center": matrix.middle_points.right_center,
    "right_corner.l": matrix.right_corner.l,
    "right_corner.c": matrix.right_corner.c,
    "right_corner.r": matrix.right_corner.r,
  };
});
const stageWidth = ref(0);
const stageHeight = ref(0);
const schemaConfig = computed(() => {
  if (!containerRef.value || !schemaImage.value) {
    return {
      width: 0,
      height: 0,
    };
  }
  return {
    image: schemaImage.value,
    width: stageWidth.value,
    height: stageHeight.value,
  };
});
const absoluteCircles = computed(() => {
  if (!props.matrix) {
    return [];
  }
  const imgWidth = schemaConfig.value.width || 0;
  const imgHeight = schemaConfig.value.height || 0;

  return relativeMatrixPoints.map((point) => {
    const displayValue = flatMatrix.value[point.matrixKey] ?? 0;
    const textSize =
      point.textSize *
      (schemaItemMultiply.value > 1 ? 1 : schemaItemMultiply.value);

    return {
      id: point.id,
      text: displayValue,
      x: imgWidth * point.rx,
      y: imgHeight * point.ry,
      r: point.r * schemaItemMultiply.value,
      fill: point.fill,
      border: point.border,
      textColor: point.textColor,
      textSize: textSize,
    };
  });
});
</script>

<template>
  <CardComponent>
    <template #content>
      <div class="w-full h-full" ref="containerRef">
        <v-stage
          ref="stageRef"
          :config="{ width: stageWidth, height: stageHeight }"
        >
          <v-layer>
            <v-image :config="schemaConfig" />

            <v-group
              v-for="circle in absoluteCircles"
              :key="circle.id"
              v-memo="[circle.x, circle.y, circle.text]"
              :config="{ x: circle.x, y: circle.y, listening: false }"
            >
              <v-circle
                :config="{
                  x: 0,
                  y: 0,
                  radius: circle.r,
                  fill: circle.fill,
                  stroke: circle.border,
                  strokeWidth: 1,
                }"
              />

              <v-text
                :config="{
                  x: 0,
                  y: 0,
                  text: circle.text,
                  fontSize: circle.textSize,
                  fill: circle.textColor,
                  fontStyle: 'bold',
                  width: 32,
                  height: 32,
                  offsetX: 16,
                  offsetY: 16,
                  align: 'center',
                  verticalAlign: 'middle',
                  transformsEnabled: 'position',
                }"
              />
            </v-group>
          </v-layer>
        </v-stage>
      </div>
    </template>
  </CardComponent>
</template>
